// Organizations (GET /organization list + detail + CRUD) and the recycler's
// supply targets ("appetite"), typed as the market's OrganizationSchema.

import {
  OrganizationType,
  type OrganizationRequest,
  type OrganizationSchema,
  type UpdateOrganizationRequest,
} from '../../types/organization.type';
import {
  RECYCLER_APPETITE_MASS_UNITS,
  type RecyclerAppetiteLineStored,
  type RecyclerAppetitePatchRequest,
  type RecyclerAppetiteRepeat,
  type RecyclerAppetiteStored,
} from '../../types/recycler-appetite.type';
import {
  DEMO_APPETITE_LINES,
  DEMO_FACILITIES,
  DEMO_OUTLET_ORG,
  DEMO_OUTLET_ORG_ID,
  DEMO_RECYCLER_ORG,
  DEMO_RECYCLER_ORG_ID,
  HOUR_MS,
  daysAgo,
  iso,
  materialById,
  type DemoPersona,
  type DemoSeedOrganization,
} from '../core';
import { isInBasket } from './basket';
import { fail } from './errors';

const DEMO_ORG_IDS = new Set([DEMO_OUTLET_ORG_ID, DEMO_RECYCLER_ORG_ID]);

function seedData(org: DemoSeedOrganization): Record<string, unknown> {
  return {
    name_ar: org.name_ar,
    city: org.city,
    country: org.country,
    email: org.email,
    phone: org.phone,
    cr_number: org.cr_number,
    vat_number: org.vat_number,
  };
}

function baseOrganizations(): OrganizationSchema[] {
  const sulay = DEMO_FACILITIES[0];
  return [
    {
      id: DEMO_RECYCLER_ORG_ID,
      name: DEMO_RECYCLER_ORG.name,
      organization_type: OrganizationType.RECYCLER,
      google_maps_url: `https://maps.google.com/?q=${sulay.lat},${sulay.lng}`,
      data: {
        ...seedData(DEMO_RECYCLER_ORG),
        address: sulay.address,
        facilities: DEMO_FACILITIES.map((f) => ({ id: f.id, name: f.name_en, name_ar: f.name_ar, city: f.city })),
        mwan_licence_no: 'MWAN-REC-2024-00417',
      },
      logo_url: null,
      logo_filename: null,
      created_at: iso(daysAgo(912)),
      // Day-anchored, so the row is identical across reloads (Layout compares updated_at).
      updated_at: iso(new Date(daysAgo(1).getTime() + 9.5 * HOUR_MS)),
    },
    {
      id: DEMO_OUTLET_ORG_ID,
      name: DEMO_OUTLET_ORG.name,
      organization_type: OrganizationType.WASTE_OUTLET,
      google_maps_url: 'https://maps.google.com/?q=24.7005,46.68',
      data: {
        ...seedData(DEMO_OUTLET_ORG),
        address: 'Olaya St, Al Olaya, Riyadh 12214',
      },
      logo_url: null,
      logo_filename: null,
      created_at: iso(daysAgo(1004)),
      updated_at: iso(daysAgo(9)),
    },
  ];
}

function baseAppetite(): RecyclerAppetiteStored {
  return {
    accepts_additional_quantity: true,
    repeat: 'weekly',
    lines: DEMO_APPETITE_LINES.map((line) => ({
      material_id: materialById(line.material_id)?.material_type_id ?? line.material_id,
      parent_category: materialById(line.material_id)?.category_code ?? 'other',
      quantity: line.target_kg,
      unit: 'kg',
    })),
  };
}

let organizations: OrganizationSchema[] = [];
let appetite: RecyclerAppetiteStored = baseAppetite();
let createdCount = 0;

export function resetOrganizations(): void {
  organizations = baseOrganizations();
  appetite = baseAppetite();
  createdCount = 0;
}

/** The org as the API returns it: the recycler carries its supply targets in data.recycler_appetite. */
function present(org: OrganizationSchema): OrganizationSchema {
  if (org.id !== DEMO_RECYCLER_ORG_ID) return org;
  return { ...org, data: { ...org.data, recycler_appetite: getAppetite() } };
}

export function listOrganizations(): OrganizationSchema[] {
  return organizations.map(present);
}

export function findOrganization(id: string): OrganizationSchema | undefined {
  const org = organizations.find((o) => o.id === id);
  return org ? present(org) : undefined;
}

export function requireOrganization(id: string): OrganizationSchema {
  const org = findOrganization(id);
  if (!org) fail(404, 'Organization not found');
  return org;
}

export function isRecyclerOrg(id: string): boolean {
  return organizations.some((o) => o.id === id && o.organization_type === OrganizationType.RECYCLER);
}

/** The organization DemoEntry seeds as selectedOrganization (identical to the list row). */
export function demoOrganizationFor(persona: DemoPersona): OrganizationSchema {
  if (organizations.length === 0) resetOrganizations();
  return requireOrganization(persona === 'outlet' ? DEMO_OUTLET_ORG_ID : DEMO_RECYCLER_ORG_ID);
}

export function createOrganization(req: Partial<OrganizationRequest>): OrganizationSchema {
  const name = typeof req.name === 'string' ? req.name.trim() : '';
  if (!name) fail(422, 'Organization name is required');
  createdCount += 1;
  const now = iso(new Date());
  const org: OrganizationSchema = {
    id: `00000000-0000-4000-8000-00000000a${String(100 + createdCount).padStart(3, '0')}`,
    name,
    organization_type: req.organization_type ?? OrganizationType.RECYCLER,
    google_maps_url: req.google_maps_url ?? '',
    data: { ...(req.data ?? {}) },
    logo_url: null,
    logo_filename: req.logo_filename ?? null,
    created_at: now,
    updated_at: now,
  };
  organizations = [...organizations, org];
  return present(org);
}

export function updateOrganization(id: string, req: Partial<UpdateOrganizationRequest>): OrganizationSchema {
  const current = organizations.find((o) => o.id === id);
  if (!current) fail(404, 'Organization not found');
  const { recycler_appetite: _ignored, ...data } = { ...current.data, ...(req.data ?? {}) };
  const next: OrganizationSchema = {
    ...current,
    name: typeof req.name === 'string' && req.name.trim() ? req.name.trim() : current.name,
    organization_type: req.organization_type ?? current.organization_type,
    google_maps_url: req.google_maps_url ?? current.google_maps_url,
    logo_filename: req.logo_filename === undefined ? current.logo_filename : req.logo_filename,
    data,
    updated_at: iso(new Date()),
  };
  organizations = organizations.map((o) => (o.id === id ? next : o));
  return present(next);
}

export function deleteOrganization(id: string): void {
  if (DEMO_ORG_IDS.has(id)) fail(403, 'The demo organizations cannot be deleted');
  if (!organizations.some((o) => o.id === id)) fail(404, 'Organization not found');
  organizations = organizations.filter((o) => o.id !== id);
}

// ── Supply targets ─────────────────────────────────────────────────────────

export function getAppetite(): RecyclerAppetiteStored {
  return {
    accepts_additional_quantity: appetite.accepts_additional_quantity,
    repeat: appetite.repeat,
    lines: (appetite.lines ?? []).map((line) => ({ ...line })),
  };
}

const MASS_UNITS = RECYCLER_APPETITE_MASS_UNITS as readonly string[];

/** PATCH /organization/:org/recycler-appetite. The request carries the whole target. */
export function patchAppetite(orgId: string, body: RecyclerAppetitePatchRequest): OrganizationSchema {
  requireOrganization(orgId);
  if (!isRecyclerOrg(orgId)) fail(422, 'Recycler appetite is only available for recycler organizations');

  const repeat: RecyclerAppetiteRepeat =
    body.repeat === 'monthly' || body.repeat === 'weekly' ? body.repeat : appetite.repeat ?? 'weekly';
  const accepts =
    typeof body.accepts_additional_quantity === 'boolean'
      ? body.accepts_additional_quantity
      : appetite.accepts_additional_quantity !== false;

  const incoming = Array.isArray(body.lines) ? body.lines : appetite.lines ?? [];
  const seen = new Set<string>();
  const lines: RecyclerAppetiteLineStored[] = incoming.map((line) => {
    const material = materialById(line.material_id);
    if (!material) fail(422, `Unknown material_type_id: ${line.material_id}`);
    if (!isInBasket(orgId, line.material_id)) {
      fail(422, `'${material.name_en}' is not one of this organization's waste types. Add it on the Materials page first.`);
    }
    if (seen.has(line.material_id)) fail(422, `'${material.name_en}' appears more than once in the supply target`);
    seen.add(line.material_id);
    const quantity = Number(line.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) fail(422, 'Every supply target line needs a quantity above zero');
    if (!MASS_UNITS.includes(line.unit)) fail(422, `A supply target is a mass: use kg, tons or lbs (got '${line.unit}')`);
    // The category is the material type's, not the caller's (as the backend stamps it).
    return { material_id: line.material_id, parent_category: material.category_code, quantity, unit: line.unit };
  });

  appetite = { accepts_additional_quantity: accepts, repeat, lines };
  organizations = organizations.map((o) => (o.id === orgId ? { ...o, updated_at: iso(new Date()) } : o));
  return requireOrganization(orgId);
}
